'use strict';
import clone from 'lodash/clone';
import isFunction from 'lodash/isFunction';
import React, {Component} from 'react';
import * as T from 'prop-types';
import {shouldStateUpdate} from '../../core-utilities/react-utils';
import {SelectOption} from './SelectOption';
import {PopoverSelectView} from './PopoverSelectView';

export class PopoverSelectComponent extends Component {
  static propTypes = {
    className: T.string,
    labelClassName: T.string,
    model: T.any,
    isSaving: T.bool.isRequired,
    onSelect: T.func.isRequired,
    options: T.shape({
      nullable: T.bool,
      multiple: T.bool,
      items: T.array,
      selectValueMap: T.object,
      getOptionFromViewValue: T.func,
      trackBy: T.func,
      getViewValueFromOption: T.func,
      getNullOption: T.func,
    }).isRequired,
    disabled: T.bool,
    readOnly: T.bool,
    showText: T.bool,
    showIcon: T.bool,
    nullLabel: T.oneOfType([
      T.string,
      T.object,
    ]).isRequired,
  };

  static defaultProps = {
    nullLabel: {code: 'default.select.null.label'},
  };

  static $inject = [
    '$rootScope',
    '$popover',
    'popoverSelectTemplateService',
  ];

  constructor(props) {
    super(props);
    const selected = PopoverSelectComponent.getSelected(props);
    this.state = {
      selected: selected,
      title: selected ? selected.label : null,
    };
    this.handleClick = this.handleClick.bind(this);
    this.cleanupPopover = this.cleanupPopover.bind(this);
  }

  handleClick(e) {
    e.stopPropagation();
    this.showOptions(e);
  }

  render() {
    return (
      <PopoverSelectView
        className={this.props.className}
        labelClassName={this.props.labelClassName}
        text={this.state.selected ? this.state.selected.label : null}
        icon={this.state.selected ? this.state.selected.icon : null}
        title={this.state.title}
        isSaving={this.props.isSaving}
        disabled={this.props.disabled}
        readOnly={this.props.readOnly}
        showText={this.props.showText}
        showIcon={this.props.showIcon}
        onClick={this.handleClick}
        nullLabel={this.props.nullLabel}
      />
    );
  }

  componentDidMount() {
    this.updateState(this.props);
  }

  componentWillReceiveProps(nextProps) {
    this.updateState(nextProps);
  }

  componentWillUnmount() {
    this.cleanupPopover();
  }

  updateState(props) {
    const selected = PopoverSelectComponent.getSelected(props);
    const nextState = {
      selected,
      title: selected ? selected.label : null,
    };
    if (shouldStateUpdate(this.state, nextState)) {
      this.setState(nextState);
    }
  }

  static getSelected(props) {
    return props.options ? props.options.selectValueMap[props.options.trackBy(props.model)] : null;
  }

  // Read the value of the select control, the implementation of this changes depending
  // upon whether the select can have multiple values and whether ngOptions is at work.
  readValue() {
    return this.state.selected ? this.state.selected.viewValue : null;
  }

  writeValue(option) {
    if (!option) {
      option = this.props.options.getNullOption();
    }

    if (this.state.selected !== option) {
      this.setState({selected: option});
    }
  }

  showOptions(event) {
    if (this.props.disabled || this.props.readOnly || this.props.isSaving)
      return;
    event.stopPropagation();
    this.updateOptions();
    // We need to pass the nativeEvent for Ionic interop compatibility
    this.optionsPopover.show(event.nativeEvent);
  }

  selectOption(selectValue) {
    const option = this.props.options.selectValueMap[selectValue];
    this.props.onSelect(option ? option.viewValue : null);

    this.optionsPopover.hide().then(this.cleanupPopover);
  }

  updateOptions() {
    const popoverSelectTemplateService = PopoverSelectComponent.dependencies.popoverSelectTemplateService;
    if (this.props.options) {
      const template = popoverSelectTemplateService.generatePopoverTemplate(this.props.options, this.props.nullLabel);
      if (!this.currentTemplate || this.currentTemplate !== template) {
        this.cleanupPopover();
        this.createPopover(template);
      }
    } else {
      this.cleanupPopover();
    }
  }

  createPopover(template) {
    this.currentTemplate = template;
    this.popoverScope = PopoverSelectComponent.dependencies.$rootScope.$new();
    this.popoverScope.select = this.selectOption.bind(this);
    this.popoverScope.selection = this.readValue();
    this.optionsPopover = PopoverSelectComponent.dependencies.$popover.fromTemplate(template, {
      scope: this.popoverScope,
    });
  }

  cleanupPopover() {
    if (this.optionsPopover && isFunction(this.optionsPopover.remove)) {
      this.optionsPopover.remove();
      this.optionsPopover = null;
    }
    if (this.popoverScope) {
      this.popoverScope.$destroy();
      this.popoverScope = null;
    }
    this.currentTemplate = null;
  }

  /**
   * Build the default PopoverOptions from items and map.
   * @param optionItems
   * @param selectValueMap
   * @param trackBy
   * @return {PopoverOptions}
   */
  static buildDefaultOptions(optionItems, selectValueMap, trackBy=PopoverSelectComponent.trackBy) {
    return {
      nullable: false,
      multiple: false,
      items: optionItems,
      selectValueMap: selectValueMap,
      trackBy,
      getOptionFromViewValue: value => selectValueMap[trackBy(value)],
      /* If the viewValue could be an object that may be mutated by the application,
       * we need to make a copy and not return the reference to the value on the option.
       */
      getViewValueFromOption: trackBy === PopoverSelectComponent.trackBy
        ? PopoverSelectComponent.getViewValueFromOption
        : option => clone(option.viewValue),
      getNullOption: PopoverSelectComponent.getNullOption,
    };
  }

  static trackBy(model) {
    return model;
  }

  static getViewValueFromOption(option) {
    return option.viewValue;
  }

  static getNullOption(nullLabel) {
    const viewValue = null;
    const selectValue = null;
    const label = nullLabel ? nullLabel : null;
    const group = null;
    const disabled = false;
    const className = 'popover-select-null';
    return new SelectOption(selectValue, viewValue, label, group, disabled, className);
  }
}

/**
 * @typedef {Object} PopoverOptions
 * @property {Boolean} nullable
 * @property {Boolean} multiple
 * @property {Array<SelectOption>} items
 * @property {Object<String, SelectOption>} selectValueMap
 * @property {Function} getOptionFromViewValue
 * @property {Function} getSelectValueFromModel
 * @property {Function} getViewValueFromOption
 * @property {Function} getNullOption
 */




// WEBPACK FOOTER //
// ./src/js/core-components/popover-select/PopoverSelectComponent.js